import type { Amendment, Institution } from '@/services/firestore';
import { addMoney, multiplyMoneyByRate } from '@/lib/utils';

function normalize(str?: string | null): string {
  if (!str) return '';
  return str
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

export function isHealthCategory(category?: string | null): boolean {
  const c = normalize(category);
  // Suporta Português e Inglês; trata acentos
  return c === 'saude' || c === 'saude/saude' || c === 'health';
}

export type HealthTotals = {
  totalAllocated: number;
  healthAllocated: number;
  nonHealthAllocated: number;
  healthMinimum: number; // mínimo exigido (50% do limite total)
  details: Array<{ amendmentId: string; institutionId: string; category?: string; isHealth: boolean; value: number }>; // para logging
};

export function computeHealthTotals(
  amendments: Amendment[],
  institutions: Institution[],
  councilorTotalLimit: number,
): HealthTotals {
  // Operar em centavos para evitar erros de ponto flutuante
  const totalAllocated = addMoney(amendments.map((a) => a.value || 0));
  const details = amendments.map((a) => {
    const inst = institutions.find((i) => i.id === a.institutionId);
    const cat = inst?.category;
    const health = isHealthCategory(cat);
    return { amendmentId: a.id, institutionId: a.institutionId, category: cat, isHealth: health, value: a.value || 0 };
  });
  const healthAllocated = addMoney(details.filter((d) => d.isHealth).map((d) => d.value));
  const nonHealthAllocated = addMoney(details.filter((d) => !d.isHealth).map((d) => d.value));
  // Mínimo de saúde: 50% do limite + R$ 1,00, preservando centavos (arredondamento half-up)
  const healthMinimum = multiplyMoneyByRate(councilorTotalLimit, 0.5) + 1;

  // Logs detalhados para rastreamento
  try {
    // eslint-disable-next-line no-console
    console.groupCollapsed('[Saúde] Totais e Itens Considerados');
    console.log('Limite total do vereador:', councilorTotalLimit);
    console.log('Mínimo exigido para Saúde (50%):', healthMinimum);
    console.table(details.map(d => ({
      emenda: d.amendmentId,
      instituicao: d.institutionId,
      categoria: d.category,
      ehSaude: d.isHealth,
      valor: d.value,
    })));
    console.log('Total alocado:', totalAllocated);
    console.log('Total Saúde:', healthAllocated);
    console.log('Total fora de Saúde:', nonHealthAllocated);
    console.groupEnd();
  } catch {}

  return { totalAllocated, healthAllocated, nonHealthAllocated, healthMinimum, details };
}

// Regra: Enquanto Saúde < mínimo, alocações fora de Saúde não podem ultrapassar o mínimo
export function wouldBreakHealthRule(
  totals: HealthTotals,
  isTargetHealth: boolean,
  proposedNonHealthDelta: number,
): boolean {
  if (isTargetHealth) return false; // alocações em Saúde sempre permitidas (sem limite máximo)
  if (totals.healthAllocated >= totals.healthMinimum) return false; // já atingiu o mínimo
  const projectedNonHealth = totals.nonHealthAllocated + Math.max(0, proposedNonHealthDelta);
  return projectedNonHealth > totals.healthMinimum;
}